import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Mail, Phone, Building2, Calendar, Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LeadActivityTimeline } from "./LeadActivityTimeline";

interface Lead {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  company?: string;
  lead_source: string;
  lead_type: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface LeadNote {
  id: string;
  note: string;
  created_at: string;
  created_by: string;
}

interface LeadDetailsDialogProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export function LeadDetailsDialog({ lead, open, onOpenChange, onUpdate }: LeadDetailsDialogProps) {
  const [status, setStatus] = useState(lead?.status || "new");
  const [newNote, setNewNote] = useState("");
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      loadNotes();
    }
  }, [lead]);

  const loadNotes = async () => {
    if (!lead) return;

    const { data, error } = await supabase
      .from("lead_notes")
      .select("*")
      .eq("lead_id", lead.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading notes:", error);
    } else {
      setNotes(data || []);
    }
  };

  const handleStatusUpdate = async () => {
    if (!lead || status === lead.status) return;

    setLoading(true);
    try {
      // Insert status history
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from("lead_status_history").insert({
        lead_id: lead.id,
        old_status: lead.status,
        new_status: status,
        changed_by: user?.id,
      });

      // Update lead status
      const { error } = await supabase
        .from("leads")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", lead.id);

      if (error) throw error;

      toast.success("Status updated successfully");
      onUpdate();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!lead || !newNote.trim()) return;

    setAddingNote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase.from("lead_notes").insert({
        lead_id: lead.id,
        note: newNote.trim(),
        created_by: user?.id,
      });

      if (error) throw error;

      toast.success("Note added successfully");
      setNewNote("");
      loadNotes();
      onUpdate();
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "default";
      case "contacted":
        return "secondary";
      case "qualified":
        return "default";
      case "converted":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lead Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Contact Information */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{lead.email}</p>
                </div>
              </div>
              {lead.name && (
                <div className="flex items-center gap-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{lead.name}</p>
                  </div>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{lead.phone}</p>
                  </div>
                </div>
              )}
              {lead.company && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{lead.company}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(lead.created_at)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Status Management */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Status Management</h3>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleStatusUpdate}
                disabled={loading || status === lead.status}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Status
              </Button>
            </div>
            <div className="mt-2">
              <Badge variant={getStatusColor(lead.status)}>Current: {lead.status}</Badge>
            </div>
          </Card>

          {/* Notes Section */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Notes</h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a note about this lead..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  size="sm"
                >
                  {addingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {notes.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {notes.map((note) => (
                      <div key={note.id} className="p-2 bg-muted rounded-md">
                        <p className="text-sm">{note.note}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(note.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Activity Timeline */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Activity Timeline</h3>
            <LeadActivityTimeline leadEmail={lead.email} leadId={lead.id} />
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
